import { createGeminiImage, isGeminiAvailable } from './gemini-client.js';

const paletteCopy = {
  cloud: {
    label: 'Creamy cloud',
    prompt: 'soft cream, pearl white, champagne blush with gold foil flecks'
  },
  sakura: {
    label: 'Sakura peach',
    prompt: 'warm blush pink, coral gradients, a hint of sakura petal translucency'
  },
  forest: {
    label: 'Mossy wood',
    prompt: 'moss green, cedar brown, muted fern gradients'
  },
  moonlit: {
    label: 'Moonlit silver',
    prompt: 'cool moonstone silver, glacier blue, iridescent glow'
  }
};

const motifCopy = {
  clover: 'tiny clover sprigs and dotted lines',
  moon: 'crescent moon halo with subtle stardust',
  ribbon: 'ribbon frame with hand-drawn scallops',
  wheat: 'delicate wheat ears and pencil hatching'
};

const moodCopy = {
  gentle: 'gentle journaling aesthetic, rounded serif lettering',
  regal: 'ornate invitation typography, centered alignment',
  playful: 'whimsical doodle, rounded sans-serif',
  modern: 'minimal grid layout, generous white space'
};

const elements = {
  form: document.querySelector('[data-name-card-form]'),
  nameInput: document.querySelector('#card-name'),
  taglineInput: document.querySelector('#card-tagline'),
  paletteSelect: document.querySelector('#card-palette'),
  moodSelect: document.querySelector('#card-mood'),
  motifSelect: document.querySelector('#card-motif'),
  status: document.querySelector('[data-status]'),
  preview: document.querySelector('[data-result-img]'),
  downloadBtn: document.querySelector('[data-download]'),
  linkBack: document.querySelector("[data-name-tool-link]")
};

let geminiReady = true;
let currentImage = null;

function setStatus(message, isError = false) {
  if (!elements.status) return;
  elements.status.textContent = message;
  elements.status.classList.toggle('error', Boolean(isError));
}

function setPreview(base64Image) {
  if (!elements.preview || !elements.downloadBtn) return;
  if (!base64Image) {
    elements.preview.style.backgroundImage = '';
    elements.preview.classList.add('placeholder');
    elements.downloadBtn.classList.add('is-hidden');
    currentImage = null;
    return;
  }

  const dataUrl = `data:image/png;base64,${base64Image}`;
  currentImage = dataUrl;
  elements.preview.style.backgroundImage = `url(${dataUrl})`;
  elements.preview.classList.remove('placeholder');
  elements.downloadBtn.href = dataUrl;
  elements.downloadBtn.download = 'croissant-name-card.png';
  elements.downloadBtn.classList.remove('is-hidden');
}

function buildPrompt(values) {
  const { name, tagline, palette, mood, motif } = values;
  const palettePrompt = paletteCopy[palette]?.prompt || paletteCopy.cloud.prompt;
  const moodPrompt = moodCopy[mood] || moodCopy.gentle;
  const motifPrompt = motifCopy[motif] || motifCopy.clover;

  let prompt = `Design a printable rabbit name card for the name "${name}". `;
  prompt += `Use ${palettePrompt} as the color palette, ${moodPrompt}, and incorporate ${motifPrompt}. `;
  prompt += 'Add a tiny bunny silhouette embossed near the bottom corner. ';
  prompt += 'Layout should feel handmade yet polished, on textured cotton paper photographed in soft daylight.';
  if (tagline) {
    prompt += ` Include the phrase "${tagline}" in small supporting text.`;
  }
  return prompt;
}

async function handleSubmit(event) {
  event.preventDefault();
  if (!geminiReady) {
    setStatus('Image service is offline—please try again soon.', true);
    return;
  }
  const name = elements.nameInput?.value.trim();
  const tagline = elements.taglineInput?.value.trim();
  if (!name) {
    setStatus('Please enter your bunny’s name first.', true);
    return;
  }

  const values = {
    name,
    tagline,
    palette: elements.paletteSelect?.value || 'cloud',
    mood: elements.moodSelect?.value || 'gentle',
    motif: elements.motifSelect?.value || 'clover'
  };

  const prompt = buildPrompt(values);

  try {
    setStatus('Illustrating the name card—this takes about 10-15 seconds…');
    setPreview('');
    const output = await createGeminiImage({ prompt });
    setPreview(output);
    setStatus('Done! Download the PNG or keep generating more cards.');
  } catch (error) {
    console.error(error);
    setStatus(error.message || 'Generation failed, please try again later.', true);
    setPreview('');
  }
}

async function initAvailability() {
  geminiReady = await isGeminiAvailable();
  if (!geminiReady) {
    setStatus('Image service is offline, so the tool stays in preview mode.');
    elements.form?.classList.add('tool-disabled');
  }
}

function applyQueryDefaults() {
  const params = new URLSearchParams(window.location.search);
  const name = params.get('name');
  const tags = params.get('tags');
  if (name && elements.nameInput) {
    elements.nameInput.value = name;
  }
  if (tags) {
    if (tags.includes('celestial')) elements.paletteSelect.value = 'moonlit';
    if (tags.includes('floral')) elements.paletteSelect.value = 'sakura';
    if (tags.includes('woodland')) elements.paletteSelect.value = 'forest';
    if (tags.includes('modern')) elements.moodSelect.value = 'modern';
    if (tags.includes('regal')) elements.moodSelect.value = 'regal';
  }
}

function init() {
  if (!elements.form) return;
  initAvailability();
  applyQueryDefaults();
  elements.linkBack?.setAttribute('href', 'rabbit-name-studio.html');
  elements.form.addEventListener('submit', handleSubmit);
  elements.downloadBtn?.addEventListener('click', () => {
    if (!currentImage) {
      setStatus('Generate a card before downloading.', true);
    }
  });
}

init();
